/**
 * First-launch privacy gate.
 *
 * Wraps the whole router so it runs BEFORE any route (login included) can
 * render. On every native launch it checks the persisted `privacyPolicyAccepted`
 * flag:
 *
 *   accepted      → render the app (router decides login vs. role home)
 *   not accepted  → render the blocking <PrivacyConsent> screen
 *
 * Because the router isn't mounted while consent is pending, there's no route to
 * deep-link or navigate around — the gate can't be bypassed.
 *
 * The gate is a no-op on the web build: "first launch after install" is a native
 * app-store concept, and the existing public `/privacy` page already serves web
 * users. Flip the `isNativePlatform()` check to test it in a browser.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import PrivacyConsent from "@/pages/auth/PrivacyConsent";
import { getPrivacyAccepted } from "@/lib/privacy-consent";

type GateState = "loading" | "accepted" | "pending";

export function PrivacyConsentGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(() =>
    Capacitor.isNativePlatform() ? "loading" : "accepted",
  );

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let alive = true;
    void getPrivacyAccepted().then((accepted) => {
      if (alive) setState(accepted ? "accepted" : "pending");
    });
    return () => {
      alive = false;
    };
  }, []);

  if (state === "loading") {
    // Brief spinner while reading Preferences — matches the app's route fallback.
    return (
      <div className="grid min-h-svh place-items-center bg-bg">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (state === "pending") {
    return <PrivacyConsent onAccept={() => setState("accepted")} />;
  }

  return <>{children}</>;
}
